import { ErrorFallback } from "@/ErrorHandling/ErrorFallback";
import { LoginErrorPage } from "@/ErrorHandling/LoginErrorPage";
import { LoginCallback } from "@okta/okta-react";
import { wrapCreateBrowserRouterV6 } from "@sentry/react";
import { Route, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import {
  EnhancedRoute,
  ErrorBoundaryWrapper,
  RedirectToLandingPage,
  RouteDefinition,
  SecureRoute,
  makeLazyloadComponent as make,
} from "./routing";
import { Loader } from "./ui-components/Loader";
import { DashboardLayout } from "./ui-components/layouts/DashboardLayout";

const routeDefaults: RouteDefinition = {
  exact: true,
  requiresAuth: true,
  layout: DashboardLayout,
  content: () => <></>,
  path: "/",
};

const route404: RouteDefinition = {
  ...routeDefaults,
  path: "*",
  content: () => <ErrorFallback error={{ error: { status: 404, data: undefined } }} />,
};

const createSentryRouter = wrapCreateBrowserRouterV6(createBrowserRouter);

const authRoutes = (
  <Route
    path="login/callback"
    element={<LoginCallback loadingElement={<Loader />} errorComponent={LoginErrorPage} />}
  />
);

const managementRoutes = (
  <>
    <Route
      path="/companies"
      Component={make(() => import("./features/corporations/features/list/CompaniesListPage"))}
    />
    <Route path="/users" Component={make(() => import("./features/users/UsersManagementPage"))} />
    <Route path="/back-office" Component={make(() => import("./features/admin/BackOfficePanelPage"))} />
  </>
);

const siteRoutes = (
  <Route path="/sites" Component={make(() => import("./features/sites/SiteBasePage"))}>
    <Route path="/sites/add" Component={make(() => import("./features/sites/SiteAdd"))} />
    <Route path="/sites/:id" Component={make(() => import("./features/sites/SiteEdit"))} />
    <Route path="/sites" Component={make(() => import("./features/sites/SiteList"))} />
  </Route>
);

const assessmentRoutes = (
  <Route path="/assessments" Component={make(() => import("./features/assessments/AssessmentBasePage"))}>
    <Route
      path="/assessments/add"
      Component={make(() => import("./features/assessments/features/creation/AssessmentAdd"))}
    />
    <Route
      path="/assessments/:assessmentId/:siteAssessmentId"
      Component={props => {
        const Component = make(() => import("./features/site-assessment/SiteAssessmentLandingPage"));
        return <Component {...props} mode="assessments" />;
      }}
    />
    <Route
      path="/assessments/:assessmentId/:siteAssessmentId/review"
      Component={props => {
        const Component = make(() => import("./features/site-assessment/SiteAssessmentLandingPage"));
        return <Component {...props} mode="review" />;
      }}
    />
    <Route
      path="/assessments/:assessmentId/:siteAssessmentId/:taskId/review"
      Component={make(() => import("./features/assessments/features/review/TaskReviewPage"))}
    />
    <Route
      path="/assessments/:assessmentId/:siteAssessmentId/:taskId"
      Component={make(() => import("./features/assessments/features/answers/TaskInputPage"))}
    />
    <Route
      path="/assessments/:assessmentId/:siteId/assign"
      Component={make(() => import("./features/assessments/features/assign_themes/ThemeAssign"))}
    />
    <Route
      path="/assessments/:assessmentId/reviewers"
      Component={make(() => import("./features/assessments/features/assign_reviewers/AssignReviewers"))}
    />
    <Route
      path="/assessments/:assessmentId/reviewers/:stepId"
      Component={make(() => import("./features/assessments/features/assign_reviewers/AssignReviewersDetailPage"))}
    />
    <Route
      path="/assessments/:assessmentId/protocol_questions/tasks"
      Component={make(() => import("./features/assessments/features/comparison_view/TaskComparisonRedirectPage"))}
    />
    <Route
      path="/assessments/:assessmentId/protocol_questions/:protocolQuestionId/tasks"
      Component={make(() => import("./features/assessments/features/comparison_view/TasksComparisonPage"))}
    />
    <Route
      path="/assessments/:assessmentId/edit"
      Component={make(() => import("./features/assessments/features/creation/AssessmentEditPage"))}
    />
    <Route
      path="/assessments/:assessmentId/overview"
      Component={make(() => import("./features/assessments/features/overview/AssessmentOverviewPage"))}
    />
    <Route
      path="/assessments/:assessmentId/overview/:siteAssessmentId/dataproviders"
      Component={make(() => import("./features/assessments/features/overview/DataProviders"))}
    />
    <Route
      path="/assessments/:assessmentId/overview/reviewers"
      Component={make(() => import("./features/assessments/features/overview/Reviewers"))}
    />
    <Route path="/assessments" Component={make(() => import("./features/assessments/features/list/AssessmentsPage"))} />
    <Route
      path="/assessments/:id"
      Component={make(() => import("./features/assessments/features/answers/TaskInputPage"))}
    />
  </Route>
);

const reportRoutes = (
  <Route path="/reports" Component={make(() => import("./features/reports/Base"))}>
    <Route path="/reports/" Component={make(() => import("./features/reports/features/list/ReportsList"))} />
    <Route
      path="/reports/assessment/:siteId/:assessmentId/:siteAssessmentId"
      Component={make(() => import("./features/reports/SiteAssessmentReportPage"))}
    />
    <Route
      path="/reports/standard/:siteId/:assessmentId/:siteAssessmentId/:standardId"
      Component={make(() => import("./features/reports/StandardReportPage"))}
    />
    <Route
      path="/reports/standard/:siteId/:assessmentId/:siteAssessmentId/:standardId/:rollup"
      Component={make(() => import("./features/reports/StandardReportPage"))}
    />
    <Route
      path="/reports/custom/:reportId"
      Component={make(() => import("./features/reports/features/custom_report/CustomReportDetails"))}
    />
  </Route>
);

const assessmentUpdateRoutes = (
  <Route
    path="/assessment_update"
    Component={make(() => import("./features/assessment_update/AssessmentUpdateBasePage"))}
  >
    <Route
      path="/assessment_update"
      Component={make(() => import("./features/assessment_update/features/list/AssessmentUpdatePage"))}
    />
  </Route>
);

const protocolSetRoutes = (
  <Route path="/protocol_sets" Component={make(() => import("./features/protocol_set/ProtocolSetBasePage"))}>
    <Route
      path="/protocol_sets"
      Component={make(() => import("./features/protocol_set/features/protocol_questions/ProtocolQuestionList"))}
    />
    <Route
      path="/protocol_sets/:protocolSetId/review"
      Component={make(() => import("./features/protocol_set/ProtocolSetReviewDraft"))}
    />
    <Route
      path="/protocol_sets/:protocolSetId/protocol_questions/reorder"
      Component={make(() => import("./features/protocol_set/features/protocol_questions/ProtocolQuestionReorder"))}
    />
    <Route
      path="/protocol_sets/:protocolSetId/protocol_questions/add"
      Component={make(() => import("./features/protocol_set/features/protocol_questions/ProtocolQuestionAdd"))}
    />
    <Route
      path="/protocol_sets/:protocolSetId/protocol_questions/:id"
      Component={make(() => import("./features/protocol_set/features/protocol_questions/ProtocolQuestionEditPage"))}
    />
    <Route
      path="/protocol_sets/:protocolSetId/protocol_questions/:id/link_requirements"
      Component={make(
        () => import("./features/protocol_set/features/protocol_questions/ProtocolQuestionLinkRequirements"),
      )}
    />
    <Route
      path="/protocol_sets/:protocolSetId/protocol_questions/:id/link_protocol_questions"
      Component={make(() => import("./features/protocol_questions/link/LinkProtocolQuestionsPage"))}
    />
    <Route
      path="/protocol_sets/:protocolSetId/requirements/:id/link_protocol_questions"
      Component={make(() => import("./features/requirements/link/RequirementLinkProtocolQuestionPage"))}
    />
    <Route
      path="/protocol_sets/requirements"
      Component={make(() => import("./features/requirements/features/list/RequirementList"))}
    />
    <Route
      path="/protocol_sets/standards/:id"
      Component={make(() => import("./features/standards/features/creation/Edit"))}
    />
    <Route
      path="/protocol_sets/standards/view/:id"
      Component={make(() => import("./features/standards/features/view/View"))}
    />
    <Route
      path="/protocol_sets/standards/add"
      Component={make(() => import("./features/standards/features/creation/Add"))}
    />
    <Route
      path="/protocol_sets/standards"
      Component={make(() => import("./features/standards/features/list/StandardsList"))}
    />
  </Route>
);

const compareRoutes = (
  <Route path="/compare" Component={make(() => import("./features/compare/CompareBasePage"))}>
    <Route
      path="/compare/protocol_sets/:protocolSetId/:firstId/:secondId"
      Component={make(() => import("./features/compare/features/duplicates/Duplicates"))}
    />
  </Route>
);

const businessIntelligenceRoutes = (
  <Route path="/business-intelligence">
    <Route
      path="/business-intelligence/conformity-heatmap"
      Component={make(
        () => import("./features/business-intelligence/features/conformity-heatmap/ConformityHeatmapPage"),
      )}
    />
  </Route>
);

export const router = createSentryRouter(
  createRoutesFromElements(
    <Route path="" element={<ErrorBoundaryWrapper />}>
      {authRoutes}
      <Route element={<SecureRoute />} path="">
        <Route path="/" element={<ErrorBoundaryWrapper />}>
          <Route path="/" element={<RedirectToLandingPage />} />
          {managementRoutes}
          {siteRoutes}
          {assessmentRoutes}
          {reportRoutes}
          {assessmentUpdateRoutes}
          {protocolSetRoutes}
          {compareRoutes}
          {businessIntelligenceRoutes}
          <Route element={<EnhancedRoute route={route404} />} path={route404.path} />
        </Route>
      </Route>
    </Route>,
  ),
);
